/**
 * SectionHeader — sub-component of SectionCard.
 *
 * Renders only the horizontal title bar at the top of a section:
 *   - Section name in caps (`section.title || "Section N"`)
 *   - `…` actions menu (Duplicate / Reorder / Delete)
 *   - Click target (when collapsed) to expand
 *
 * Background switches by state:
 *   - Collapsed → light gray
 *   - Expanded  → brandPrimary (blue) with white text
 *
 * The title and description input fields, the section-level left-border state,
 * the trigger UI, and the question list are all rendered by `SectionCard` outside
 * of this component.
 */
import { css } from 'aphrodite';
import React from 'react';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../../../../components-v2/Dropdown';
import { ChevronDown } from '../../../../../../../components/svgs/icons/ChevronDown';
import type { SurveySection } from '../../types';
import { styles } from './SectionHeader.styles';

export interface SectionHeaderProps {
	/** The section data. Used for the title display when collapsed and as a placeholder when expanded. */
	section: SurveySection;
	/** Zero-based position index of this section inside the form array. */
	index: number;
	/** Whether the parent SectionCard is currently expanded. */
	isExpanded: boolean;
	/** Section has uncommitted edits since the last save. Drives the blue bar state. */
	isDirty: boolean;
	/** Section was loaded from the BE (already persisted). Drives the green bar when clean. */
	hasBeenSaved: boolean;
	/** Section has at least one validation error. Drives the red bar state. */
	hasError: boolean;
	/** Called when the collapsed bar is clicked to expand the card. */
	onExpand: () => void;
	/** Called when the user selects "Duplicate" from the … menu. */
	onDuplicate: () => void;
	/** Called when the user selects "Delete" from the … menu. */
	onDelete: () => void;
	/**
	 * Whether the Delete menu item is rendered.
	 * False when this is the only remaining section — a V2 survey must keep ≥1 section
	 * so it cannot collapse back to the V1 editor.
	 */
	canDelete: boolean;
	/**
	 * Called when the user selects "Reorder" from the … menu.
	 * When omitted the Reorder menu item is not rendered.
	 */
	onReorder?: () => void;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
	section,
	index,
	isExpanded,
	isDirty,
	hasBeenSaved,
	hasError,
	onExpand,
	onDuplicate,
	onDelete,
	canDelete,
	onReorder,
}) => {
	// Mirrors the V1 QuestionField stripe rules — same precedence: error wins,
	// then saved-clean, then blue (new or dirty).
	const stateClass = hasError ? styles.barError : hasBeenSaved && !isDirty ? styles.barSaved : styles.barNew;
	const trimmedTitle = section.title?.trim();
	const displayTitle = (trimmedTitle && trimmedTitle.length > 0 ? trimmedTitle : `Section ${index + 1}`).toUpperCase();

	const handleBarClick = () => {
		if (!isExpanded) onExpand();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!isExpanded && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			onExpand();
		}
	};

	return (
		<div
			className={css(styles.bar, stateClass, !isExpanded && styles.barCollapsed)}
			onClick={handleBarClick}
			role={!isExpanded ? 'button' : undefined}
			tabIndex={!isExpanded ? 0 : undefined}
			onKeyDown={!isExpanded ? handleKeyDown : undefined}
		>
			<ChevronDown
				width={18}
				height={18}
				aria-hidden
				className={css(styles.chevron, !isExpanded && styles.chevronCollapsed)}
			/>
			<span className={css(styles.titleText)}>{displayTitle}</span>

			<div className={css(styles.actions)} onClick={e => e.stopPropagation()}>
				<Dropdown>
					{/*
					 * `fillColor='currentColor'` lets the ⋯ inherit the bar's text color
					 * (white on blue/red, dark `titles` on green — see styles). The
					 * `ellipsisTriggerInherit` style forces `color: inherit` on the
					 * underlying <button> so the cascade reaches the SVG; otherwise the
					 * button's user-agent `ButtonText` shows through as black.
					 */}
					<DropdownEllipsisTrigger
						aria-label='more options'
						fillColor='currentColor'
						styles={[styles.ellipsisTriggerInherit]}
					/>
					<DropdownContent align='end' styles={[styles.dropdownContentMinWidth]}>
						{!isExpanded && <DropdownItem onClick={onExpand}>View questions</DropdownItem>}
						{onReorder && <DropdownItem onClick={onReorder}>Reorder</DropdownItem>}
						<DropdownItem onClick={onDuplicate}>Duplicate</DropdownItem>
						{canDelete && <DropdownItem onClick={onDelete}>Delete</DropdownItem>}
					</DropdownContent>
				</Dropdown>
			</div>
		</div>
	);
};
